/**
 * WELCHE FARBE EIN ZUSTAND TRÄGT (2.10, Tabelle 6).
 *
 * ============================================================================
 * DER BEFUND VOM 14.09.2026
 * ============================================================================
 *
 * Die Vorlage färbt Zustände nie nach Erfolg — sie kennt zwei blasse Flächen
 * und einen Rahmen, und das war's. Unsere vier Bedeutungsfarben sind eine
 * Ergänzung aus Tabelle 6, und sie sind richtig; verteilt haben wir sie
 * großzügig.
 *
 * `.marke--kunde` war ein Alias von `.marke--gut` — ein Sachname, hinter dem
 * eine Bedeutungsfarbe steckte. Über ihn trugen am Ende „versendet", „in
 * Verhandlung", „teilbezahlt", „teilweise", „ausgeschöpft", „aktiv" und der
 * Kundenstatus „Kunde" dieselbe grüne Fläche wie „angenommen" und „bezahlt".
 * Ein Angebot, das rausgegangen ist, hat aber nichts erreicht — es läuft.
 *
 * Unsere eigene Festlegung sagt, was Grün heißt: „geprüft, übertragen,
 * freigegeben". Wer sie auf den Normalfall legt, hat für den Abschluss nichts
 * mehr übrig: Wenn zwölf von fünfzehn Zeilen grün sind, ist Grün keine
 * Auskunft mehr, sondern Tapete.
 *
 * ============================================================================
 * FÜNF ARTEN, UND SIE STEHEN HIER UND NICHT IN ACHT MASKEN
 * ============================================================================
 *
 * Vorher hatte jede Maske ihre eigene Tabelle mit KLASSENNAMEN darin — und
 * damit acht Gelegenheiten, die Regel neu zu erfinden oder sich zu vertippen.
 * Genau das ist zweimal passiert: `vorlaeufig: 'achtung'` in den Ressourcen
 * zeigte auf `.marke--achtung`, eine Regel, die es nie gab, und die Marke
 * blieb farblos (derselbe Fehler wie am 04.09.2026 auf der Statusseite).
 *
 * Jetzt nennt die Maske eine ART, keine Klasse. Ein Tippfehler ist damit ein
 * Übersetzungsfehler und keine stumme Marke.
 */
export type Markenart =
  /**
   * Der laufende, übliche Zustand — „versendet", „offen", „teilweise",
   * „aktiv". Er sagt: hier passiert gerade das Normale. Keine Bedeutungsfarbe,
   * denn er bedeutet nichts, was auffallen müsste.
   */
  | 'laufend'
  /**
   * Das Ziel des Vorgangs ist erreicht: „geprüft, übertragen, freigegeben"
   * (--gut). Angenommen, bezahlt, berechnet, festgeschrieben, übernommen.
   */
  | 'abschluss'
  /**
   * „Zulässig, aber dokumentiert; was heute abläuft" (--achtung). Etwas ist zu
   * tun, und zwar bald: abgelaufen, ausgeschöpft, in Wartung, Interessent.
   */
  | 'aufmerksamkeit'
  /**
   * „Gesperrt, etwas ist zu tun" (--fehler). Der Weg ist zu Ende oder
   * verschlossen: abgelehnt, storniert, überfällig, außer Betrieb.
   */
  | 'sackgasse'
  /**
   * Stillgelegt — ein Entwurf, eine ausgemusterte Ressource, eine abgelöste
   * Fassung. Leise Schrift, keine Farbe.
   */
  | 'stillgelegt';

const KLASSE: Readonly<Record<Markenart, string>> = {
  /* AeA-32: `tag-accent` der Vorlage — accent-100 / accent-800. */
  laufend: 'marke marke--akzent',
  abschluss: 'marke marke--gut',
  aufmerksamkeit: 'marke marke--warn',
  sackgasse: 'marke marke--krit',
  stillgelegt: 'marke marke--inaktiv',
};

/**
 * Die Klassen einer Zustandsmarke.
 *
 * WCAG 1.4.1 bleibt die Grundlage: Der Zustand steht als TEXT in der Marke,
 * die Farbe kommt hinzu. Zwei laufende Zustände dürfen deshalb gleich
 * aussehen — „offen" und „teilweise" unterscheidet der Text.
 */
export function markenklasse(art: Markenart): string {
  return KLASSE[art];
}
